// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect } from 'react';
import { auth, db } from '../utils/firebaseConfig';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { logUserActivity } from '../utils/auditLogger';
import { presenceTracker } from '../utils/presenceTracker';

export const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [role, setRole] = useState(null);
  const [userName, setUserName] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [isDeactivated, setIsDeactivated] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // User is signed in
        const userDocRef = doc(db, 'users', user.uid);
        try {
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            if (userData.active === false) {
              // User account is deactivated
              setIsDeactivated(true);
              await presenceTracker.cleanup();
              await signOut(auth);
              await logUserActivity(user.uid, userData.name, 'LOGOUT', { reason: 'Account deactivated' });
              setCurrentUser(null);
              setRole(null);
              setUserName(null);
              toast.error('Your account has been deactivated.');
            } else {
              // User account is active
              setCurrentUser(user);
              setRole(userData.role);
              setUserName(userData.name);
              setIsDeactivated(false);
            }
          } else {
            // No user document found in Firestore
            setCurrentUser(user);
            setRole(null);
            setUserName(null);
          }
        } catch (error) {
          console.error('Error fetching user document:', error);
          setCurrentUser(user);
          setRole(null);
          setUserName(null);
          toast.error('An error occurred while fetching user data.');
        }
      } else {
        // User is signed out
        if (currentUser) {
          // Only log if there was a previous user (actual logout, not initial load)
          await presenceTracker.cleanup();
          await logUserActivity(currentUser.uid, userName, 'LOGOUT', { reason: 'User initiated' });
        }
        setCurrentUser(null);
        setRole(null);
        setUserName(null);
        setIsDeactivated(false);
      }
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, [currentUser, userName]);

  const value = {
    currentUser,
    role,
    userName,
    authReady,
    isDeactivated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
